/** Fully original critter DNA derived from a transaction id. No Nintendo IP. */

import {
  bodyIndexForCreature,
  resolveKnownProgram,
} from "../data/programs";
import { isValidTxId } from "../util/txid";

export type CritterType =
  | "normal"
  | "steel"
  | "psychic"
  | "electric"
  | "ghost"
  | "grass";

export type CritterPhase = "wild" | "catching" | "caught" | "fled";

/** What the creature represents in the explorer — always a real tx. */
export type CritterRef = { kind: "tx"; txid: string };

export interface Critter {
  id: string;
  /** Explorer target — live WS transaction hash only. */
  ref: CritterRef;
  type: CritterType;
  shiny: boolean;
  legendary: boolean;
  size: number; // 0.6 – 2.2
  body: number; // silhouette family 0–5
  markings: number; // pattern 0–7
  hue: number; // 0–360
  name: string;
  feePriority: number;
  sizeBytes: number;
  /** Block tip this tx landed in — used to board the matching sea dragon. */
  blockHeight?: number;
  /** Program pubkeys from live tx event / REST (hex or base58). */
  programIds: string[];
  /** Explorer label when program is in the known map. */
  programLabel: string | null;
  /** Chain execution result when known (distinct from shore phase). */
  result: "success" | "failed" | "pending";
  addedAt: number;
  phase: CritterPhase;
  phaseChangedAt: number;
  x: number;
  y: number;
  /** Beach slot target so wild txs walk fully on-screen and spread out. */
  homeX: number;
  homeY: number;
  bob: number;
  catchProgress: number;
}

const TYPE_HUE: Record<CritterType, number> = {
  normal: 38,
  steel: 210,
  psychic: 300,
  electric: 52,
  ghost: 268,
  grass: 130,
};

const SYLLABLES_A = [
  "za",
  "ki",
  "ro",
  "me",
  "lu",
  "pa",
  "ni",
  "xo",
  "vu",
  "te",
  "sa",
  "qi",
];
const SYLLABLES_B = [
  "rch",
  "bit",
  "isle",
  "drift",
  "glow",
  "reef",
  "moss",
  "spark",
  "tide",
  "arch",
  "nest",
  "rune",
];

function hashBytes(id: string): Uint8Array {
  const out = new Uint8Array(16);
  let h1 = 0x811c9dc5;
  let h2 = 0x01000193;
  for (let i = 0; i < id.length; i++) {
    const c = id.charCodeAt(i);
    h1 ^= c;
    h1 = Math.imul(h1, 0x01000193);
    h2 ^= c + i * 17;
    h2 = Math.imul(h2, 0x85ebca6b);
  }
  for (let i = 0; i < 8; i++) {
    out[i] = (h1 >>> (i * 4)) & 0xff;
    out[8 + i] = (h2 >>> (i * 4)) & 0xff;
  }
  // Mix hex nibbles if present for extra entropy
  for (let i = 0; i < id.length && i < 32; i++) {
    const n = parseInt(id[i]!, 16);
    if (!Number.isNaN(n)) out[i % 16] ^= n << (i % 3);
  }
  return out;
}

function pickType(bytes: Uint8Array, feePriority: number): CritterType {
  if (feePriority >= 80) return "electric";
  const roll = bytes[0]! % 100;
  if (roll < 12) return "ghost";
  if (roll < 28) return "steel";
  if (roll < 44) return "psychic";
  if (roll < 60) return "grass";
  return "normal";
}

function critterName(bytes: Uint8Array): string {
  const a = SYLLABLES_A[bytes[4]! % SYLLABLES_A.length]!;
  const b = SYLLABLES_B[bytes[5]! % SYLLABLES_B.length]!;
  return a[0]!.toUpperCase() + a.slice(1) + b;
}

export function critterFromTx(input: {
  txid: string;
  feePriority?: number | null;
  sizeBytes?: number | null;
  addedAt?: string | number | null;
  blockHeight?: number | null;
  programIds?: string[] | null;
  success?: boolean | null;
}): Critter {
  if (!isValidTxId(input.txid)) {
    throw new Error("critterFromTx: refusing spawn without validated txid");
  }
  const bytes = hashBytes(input.txid);
  const feePriority = input.feePriority ?? 0;
  const sizeBytes = input.sizeBytes ?? 200;
  const type = pickType(bytes, feePriority);
  const size = Math.min(2.2, Math.max(0.6, 0.55 + sizeBytes / 900));
  const shiny = feePriority >= 50 || bytes[2]! > 250;
  const legendary = sizeBytes >= 1800 || feePriority >= 120;
  const ref: CritterRef = { kind: "tx", txid: input.txid };
  const blockHeight =
    input.blockHeight != null && input.blockHeight > 0
      ? input.blockHeight
      : undefined;
  const programIds = (input.programIds ?? [])
    .map((p) => String(p).trim())
    .filter(Boolean);
  const known = resolveKnownProgram(programIds);
  const mappedBody = known ? bodyIndexForCreature(known.body) : null;
  const body = mappedBody != null ? mappedBody : bytes[6]! % 6;
  const result =
    input.success === false
      ? "failed"
      : input.success === true
        ? "success"
        : "pending";

  return {
    id: input.txid,
    ref,
    type,
    shiny,
    legendary,
    size,
    body,
    markings: bytes[7]! % 8,
    hue: (TYPE_HUE[type] + (bytes[8]! % 24) - 12 + 360) % 360,
    name: critterName(bytes),
    feePriority,
    sizeBytes,
    blockHeight,
    programIds,
    programLabel: known?.label ?? null,
    result,
    addedAt:
      typeof input.addedAt === "number"
        ? input.addedAt
        : input.addedAt
          ? Date.parse(input.addedAt)
          : Date.now(),
    phase: "wild",
    phaseChangedAt: performance.now(),
    x: 0,
    y: 0,
    homeX: 0,
    homeY: 0,
    bob: (bytes[9]! / 255) * Math.PI * 2,
    catchProgress: 0,
  };
}

export function typeLabel(t: CritterType): string {
  return t[0]!.toUpperCase() + t.slice(1);
}
